<template>
  <location-autocomplete
    v-if="type === DestinationAutocompleteType.Search"
    v-model:search="search"
    v-model:suggestions="filteredSuggestions"
    :disabled="disabled"
    :errors="errors"
    :hide-suggestions="hideSuggestions"
    :inverse="inverse"
    :label="$t('search.filter.label.destination')"
    :message="inputHint"
    :placeholder="$t('search.filter.value.allDestination')"
    :suggestion-component="DestinationSuggestion"
    :suggestion-empty-helper="
      $t('search.filter.destination.emptyResultsHelper')
    "
    :suggestion-empty-title="$t('search.filter.destination.emptyResults')"
    :suggestion-helper="$t('search.filter.destination.helper')"
    :data-testid="$attrs['data-testid']"
    @focus="onFocus"
    @clear="onClear"
    @debounced-input="onDebounceInput"
    @suggestion-picked="onSuggestionPicked"
    @keydown.enter="onEnterKeyPressed" />

  <location-select
    v-if="type === DestinationAutocompleteType.Select"
    v-model:search="search"
    v-model:suggestions="filteredSuggestions"
    :disabled="disabled"
    :errors="errors"
    :hide-suggestions="hideSuggestions"
    :inverse="inverse"
    :label="$t('search.filter.label.destination')"
    :message="inputHint"
    :placeholder="$t('search.filter.value.allDestination')"
    :suggestion-component="DestinationSuggestion"
    :suggestion-empty-helper="
      $t('search.filter.destination.emptyResultsHelper')
    "
    :suggestion-empty-title="$t('search.filter.destination.emptyResults')"
    :suggestion-helper="$t('search.filter.destination.helper')"
    :data-testid="$attrs['data-testid']"
    @focus="onFocus"
    @clear="onClear"
    @debounced-input="onDebounceInput"
    @suggestion-picked="onSuggestionPicked"
    @keydown.enter="onEnterKeyPressed" />
</template>

<script lang="ts" setup>
import type {
  SearchDestination as GQLSearchDestination,
  SearchDestinationLocalization,
} from '~/domains/graphql';
import {
  SearchDestinationResultType,
  useSearchDestinationQuery,
} from '~/domains/graphql';
import type { SearchDestination } from '~/domains/search';
import LocationAutocomplete from '~/domains/search/components/LocationAutocomplete.vue';
import LocationSelect from '~/domains/search/components/LocationSelect.vue';
import { useLayoutStore } from '~/domains/ux';
import type { AiSuggestion } from '~/domains/ux/molecules/AiInput/interfaces';

import DestinationSuggestion from './DestinationSuggestion.vue';

enum DestinationAutocompleteType {
  Search = 'search',
  Select = 'select',
}

type Props = {
  modelValue: SearchDestination;
  disabled?: boolean;
  type?: DestinationAutocompleteType;
  errors?: string[];
  hideSuggestions?: boolean;
  inputHint?: string;
  inverse?: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  type: DestinationAutocompleteType.Select,
  errors: undefined,
  hideSuggestions: false,
  inputHint: '',
  inverse: false,
});

type Emits = {
  (event: 'update:modelValue', value: SearchDestination): void;
  (event: 'destinations-fetched', value: GQLSearchDestination[]): void;
  (event: 'keydown.enter'): void;
};
const emits = defineEmits<Emits>();

const layoutStore = useLayoutStore();
const enterKeyPressedOnce = ref(false);
const isDeepSearch = computed(
  () => props.type === DestinationAutocompleteType.Search,
);

const search = computed({
  get() {
    return props.modelValue;
  },
  set(value: SearchDestination) {
    emits('update:modelValue', value);
  },
});

const { onResult, refetch, result, start, stop } = useSearchDestinationQuery(
  {
    search: isDeepSearch.value ? (search.value.text as string) : '',
  },
  {
    fetchPolicy: 'no-cache',
  },
);

onResult(() => {
  onDestinationsFetched(
    result.value?.searchDestination?.destinations as GQLSearchDestination[],
  );
});

const onDestinationsFetched = (destinations: GQLSearchDestination[] = []) => {
  emits('destinations-fetched', destinations);

  if (process.client) {
    layoutStore.header.queryParameterLocalization = (route, language) => {
      if (typeof route.query?.destination !== 'string') return;

      const searchDestination = JSON.parse(
        route.query.destination,
      ) as SearchDestination;
      const destinationFieldMapping: Record<
        SearchDestinationResultType,
        string | undefined
      > = {
        [SearchDestinationResultType.City]: 'city',
        [SearchDestinationResultType.Country]: 'country',
        [SearchDestinationResultType.Hotel]: undefined,
      };
      const destinationField = (destinationFieldMapping[
        searchDestination.type!
      ] ?? undefined) as keyof SearchDestinationLocalization;

      if (!destinationField) return;

      const matchingDestination = destinations.find(
        destination =>
          destination.type === searchDestination.type &&
          // @ts-ignore
          destination[destinationField] === searchDestination.text,
      );

      if (!matchingDestination) return;

      const localization = (matchingDestination.localizations || []).find(
        localization => localization?.language === language,
      );

      if (!localization) return;

      route.query.destination = JSON.stringify({
        destinationId: matchingDestination.expediaId as string,
        text: localization[destinationField] as string,
        ridcode: undefined,
        type: matchingDestination.type,
      });

      return route;
    };
  }
};

const suggestions = computed<AiSuggestion[]>(() => {
  if (!result.value?.searchDestination) return [];

  return (result.value?.searchDestination?.destinations || []).map(item => {
    const suggestion = {
      key: `${item?.type}-${item?.hotel?.id}`,
      props: item,
      value: '',
    } as AiSuggestion;

    switch (item?.type) {
      case SearchDestinationResultType.Country:
        suggestion.value = item.country as string;
        break;
      case SearchDestinationResultType.City:
        suggestion.value = item.city as string;
        break;
      case SearchDestinationResultType.Hotel:
        suggestion.value = item.hotel?.name as string;
        break;
    }

    return suggestion;
  });
});

// Use a local variable to store the filtered sentences.
const showSuggestions = ref(true);
const filteredSuggestions = computed(() => {
  return showSuggestions.value ? suggestions.value : [];
});

// Function to remove focus from input.
const blurInput = () => {
  const element = document.querySelector('input[name="destination"]');
  if (element) {
    (element as HTMLInputElement).blur();
  }
};

// Function to move focus to the search button
const focusSearchButton = () => {
  nextTick(() => {
    const searchButton = document.querySelector(
      'button.BookingBar-submit',
    );
    if (searchButton) {
      (searchButton as HTMLElement).focus();
    }
  });
};

const onSuggestionPicked = (suggestion: AiSuggestion) => {
  if (isDeepSearch.value) {
    stop();
  }

  const destination = suggestion.props as GQLSearchDestination;
  const hotel = destination.hotel;

  search.value = {
    destinationId: destination.expediaId as string,
    text: suggestion.value as string,
    countryCode:
      destination.type === SearchDestinationResultType.Country
        ? (destination.countryCode as string)
        : undefined,
    ridcode:
      destination.type === SearchDestinationResultType.Hotel
        ? (hotel?.id as string)
        : undefined,
    type: destination.type as SearchDestinationResultType,
  };

  if (isDeepSearch.value) {
    result.value = undefined;
  }

  // Remove focus after selecting a proposal
  nextTick(() => {
    blurInput();
    focusSearchButton();
  });
};

const onFocus = () => {
  enterKeyPressedOnce.value = false;
  showSuggestions.value = true;
  if (isDeepSearch.value) {
    refetch({
      search: search.value.text as string,
    });
  }
};

const onClear = () => {
  search.value = { text: '' };
  result.value = undefined;
  onDestinationsFetched();
  start();
  enterKeyPressedOnce.value = false;
  showSuggestions.value = true;
};

const onDebounceInput = () => {
  enterKeyPressedOnce.value = false;
  showSuggestions.value = true;
  if (!isDeepSearch.value) return;

  if (search.value.text) {
    refetch({
      search: search.value.text as string,
    });
  } else {
    result.value = undefined;
    onDestinationsFetched();
  }
};

const onEnterKeyPressed = (event: KeyboardEvent) => {
  if (!enterKeyPressedOnce.value) {
    event.preventDefault();
    event.stopPropagation();

    // First Enter press
    showSuggestions.value = false;

    // If there are suggestions, select the first one
    if (suggestions.value.length > 0) {
      onSuggestionPicked(suggestions.value[0]);
    } else {
      search.value = { text: '' };
      result.value = undefined;
      onDestinationsFetched();
    }

    // In any case, blur the input and focus the search button
    nextTick(() => {
      blurInput();
      focusSearchButton();
    });

    enterKeyPressedOnce.value = true;
  } else {
    // For the second Enter press, we'll let the event propagate
    // to trigger the search button click
    emits('keydown.enter');
  }
};
</script>
